import React from 'react'
import { Modal, Form, Col, Row, Button, Tabs, Tab, ListGroup } from 'react-bootstrap'
import { createModal, ModalResolveType, useModalAction } from '@src/lib/modal-manager'
import { useFetch } from '@src/hooks'
import { useForm, Controller } from '@src/lib/react-hook-form'
import z from 'zod'
import { CarService } from '@src/domain/services'
import { omit } from '@src/lib/lodash'

interface EditSfxProps {
  dtoUuid?: string;
  itemDto?: SfxDto;
}

interface SfxDto {
  carBrandUuid: string,
  carModelUuid: string,
  sfxNo: string,
  description?: string,
  carPartUuids: string[],
  pioItemUuids: string[],
  status: string
}

const initState: SfxDto = {
  carBrandUuid: '',
  carModelUuid: '',
  sfxNo: '',
  description: '',
  carPartUuids: [],
  pioItemUuids: [],
  status: '1'
}

export function EditSfxModalWrapper(props: EditSfxProps) {
  const { resolve, remove, hide } = useModalAction()
  const { dtoUuid, itemDto } = props
  
  const { register, handleSubmit, watch, formState: { errors }, control } = useForm<SfxDto>({
    reValidateMode: 'onSubmit',
    defaultValues: {...initState, ...itemDto},
    rules: z.object({
      carBrandUuid: z.string().min(1, { message: '請選擇品牌' }),
      carModelUuid: z.string().min(1, { message: '請選擇車種' }),
      sfxNo: z.string().min(1, { message: '請輸入SfxNo' }),
      description: z.string().optional(),
      carPartUuids: z.string().array().min(1, { message: '' }),
      pioItemUuids: z.string().array().min(1, { message: '' }),
      status: z.string().min(1, {message: '請選擇狀態'})
    })
  })
  const carBrandUuid = watch('carBrandUuid')

  const { data: carBrandOptions = [] } = useFetch(CarService.getCarBrands, null)

  const { data: carModelOptions = [] } = useFetch(
    async function getCarModelOptions(params: CarService.GetCarModelsByCarBrandParams) {
      if (!params.uuid) return []

      const carModelList = await CarService.getCarModelsByCarBrand(params)

      return carModelList
    },
    { uuid: carBrandUuid }
  )

  const { data: pioItemOptions = [] } = useFetch(CarService.getPioItems, null)
  
  const { data: carPartOptions = [] } = useFetch(CarService.getCarParts, null)

  const handleClose = () => {
    resolve(ModalResolveType.CLOSED)
    hide()
    remove()
  }

  const handleCancel = () => {
    resolve(ModalResolveType.CANCEL)
    hide()
    remove()
  }

  const handleOnSubmit = (formDto: SfxDto) => {
    resolve(ModalResolveType.OK, omit(formDto, 'carBrandUuid'))
    hide()
    remove()
  }

  return (
    <Modal size='lg' show onHide={handleClose} centered>
      <Form onSubmit={handleSubmit(handleOnSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>{`${dtoUuid ? '編輯' : '新增'}規格(sfx)`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              品牌
            </Form.Label>
            <Col sm="10">
            
              <Controller
                name='carBrandUuid'
                control={control}
                render={({ field }) => <Form.Select {...field}>
                  {[{uuid: '', code: ''}, ...carBrandOptions].map(option => <option key={option.uuid} value={option.uuid}>{option.code}</option>)}
                </Form.Select>}
              />
            </Col>
            {errors.carBrandUuid && <Form.Text className='text-danger'>{errors.carBrandUuid.message}</Form.Text>}
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              車種-年式
            </Form.Label>
            <Col sm="10">
            
              <Controller
                name='carModelUuid'
                control={control}
                render={({ field }) => <Form.Select {...field}
              >
                  <option value=''>請選擇車種</option>
                  {carModelOptions.map(option => <option key={option.uuid} value={option.uuid}>{option.description}</option>)}
                </Form.Select>}
              />
            </Col>
            {errors.carModelUuid && <Form.Text className='text-danger'>{errors.carModelUuid.message}</Form.Text>}
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              SFXNo
            </Form.Label>
            <Col sm="10">
              <Form.Control placeholder="HGG" {...register('sfxNo')} />
            </Col>
            {errors.sfxNo && <Form.Text className='text-danger'>{errors.sfxNo.message}</Form.Text>}
          </Form.Group>
          <Tabs defaultActiveKey="car-part">
            <Tab className="mb-3" eventKey='car-part' title="組裝部位">
              <ListGroup style={{ height: '20rem', maxHeight: '36rem', overflowY: 'auto', borderRadius: '0' }}>
                {
                  carPartOptions.map(carPart => {
                    return <ListGroup.Item key={carPart.uuid}>
                      <Form.Check
                        type='checkbox'
                        value={carPart.uuid}
                        label={`${carPart.code}-${carPart.description}`}
                        {...register("carPartUuids")}
                      />
                    </ListGroup.Item>
                  })
                }
              </ListGroup>
            </Tab>
            <Tab className="mb-3" eventKey="pio-item" title="組裝項目">
              <ListGroup style={{ height: '20rem', maxHeight: '36rem', overflowY: 'auto', borderRadius: '0' }}>
                {
                  pioItemOptions.map(pioItem => {
                    return <ListGroup.Item style={{ border: 'none' }} key={pioItem.uuid}>
                      <Form.Check
                        type='checkbox'
                        value={pioItem.uuid}
                        label={pioItem.description}
                        {...register("pioItemUuids")}
                      />
                    </ListGroup.Item>
                  })
                }
              </ListGroup>
            </Tab>
          </Tabs>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              狀態
            </Form.Label>
            <Col sm='10'>
              <Form.Select {...register('status')}>
                <option value='1'>啟用</option>
                <option value='0'>停用</option>
              </Form.Select>
            </Col>
            {errors.status && <Form.Text className='text-danger'>{errors.status.message}</Form.Text>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCancel} variant="light">
            取消
          </Button>
          <Button type='submit'>
            確認
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const EditSfxModal = createModal(EditSfxModalWrapper)
